import { z } from 'zod/v4'
import { InputValidation, validInput } from './input'
import { AdUnitEnv } from './pbs/adunit'
import { ScopeId } from './scope'
import { Stack } from './stack'

export const SITE_ENVS = ['web', 'ios', 'android', 'amp'] as const
const SiteEnvSchema = z.enum(SITE_ENVS)
export type SiteEnv = z.infer<typeof SiteEnvSchema>

export const isSiteEnv = (value: unknown): value is SiteEnv => SITE_ENVS.includes(value as SiteEnv)

export const SITE_ENV_LABELS: Record<SiteEnv, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  amp: 'AMP',
}

export const SITE_STATUSES = ['to-configure', 'out-of-sync', 'to-deploy', 'up-to-date', 'inactive'] as const
export const SiteStatusSchema = z.enum(SITE_STATUSES)

export const SITE_CONTROL_CENTER_STATUSES = ['to-configure', 'out-of-sync', 'to-deploy', 'up-to-date'] as const
export const SiteControlCenterStatusSchema = z.enum(SITE_CONTROL_CENTER_STATUSES)

// Ad management ships its own Prebid build, exposed on the page under this global variable.
// Sites running it must point the analytics tag to that variable instead of the publisher one.
export const KLEANADS_PBJS_VARIABLE_NAME = 'kleanadsPbjs'

export const SITE_AD_UNIT_ENV_COMPATIBILITY: Record<SiteEnv, AdUnitEnv[]> = {
  web: ['web', 's2s'],
  ios: ['inapp'],
  android: ['inapp'],
  amp: ['amp'],
}

/**
 * How a media list narrows by product activation. `'pbs'` and `'adm'` mean "on this product, or on
 * none yet", which is what each product's own list shows; `'any'` means "on some product", which is
 * what a server-side bidder seat maps over. Omitting the filter entirely means every media.
 */
export const SITE_PRODUCT_FILTERS = ['pbs', 'adm', 'any'] as const
export type SiteProductFilter = (typeof SITE_PRODUCT_FILTERS)[number]
export const isSiteProductFilter = (value: unknown): value is SiteProductFilter => SITE_PRODUCT_FILTERS.includes(value as SiteProductFilter)

/** Which flags say a media is activated on a product. Neither set means Analytics: measured, never deployed. */
export type SiteActivationFlags = Pick<Site, 'adManagementEnabled' | 'pbsStandaloneStatus'>

export const isPbsEnabled = (site: SiteActivationFlags): boolean => !!site.pbsStandaloneStatus && site.pbsStandaloneStatus !== 'inactive'

export const isAdManagementEnabled = (site: SiteActivationFlags): boolean => !!site.adManagementEnabled

/**
 * Which product a media belongs to. There is no product field on a `Site` — it is derived from the two
 * activation flags, and a media carrying neither is still Analytics-only.
 *
 * In `common` rather than in the list that renders it: the media list now narrows by this, and a Mongo
 * filter that disagreed with the column beside it would hide rows the reader can see.
 */
export const SITE_PRODUCTS = ['adm', 'pbs', 'analytics'] as const
export type SiteProduct = (typeof SITE_PRODUCTS)[number]
export const isSiteProduct = (value: unknown): value is SiteProduct => SITE_PRODUCTS.includes(value as SiteProduct)

/** One wins when both flags are set: medias created before the XOR rule can carry either or both. */
export const siteProductOf = (site: SiteActivationFlags): SiteProduct => (isAdManagementEnabled(site) ? 'adm' : isPbsEnabled(site) ? 'pbs' : 'analytics')

const BOOT_BASE_URL = 'https://boot.pbstck.com/v1'

/**
 * The script that connects a media to Pubstack.
 *
 * Two paths, not three: One's medias boot through `/adm`, which carries the ad management runtime as
 * well as the analytics one; Node and Analytics medias share the plain `/tag`. The snippet used to be
 * written out at each of the half-dozen places that show it, which is how the two paths drifted apart —
 * it lives here now because the media export prints it too, and a CSV disagreeing with the flyout beside
 * it would hand a publisher a tag that boots the wrong runtime.
 *
 * Web only. In-app and AMP medias are not connected with a script tag at all, and the callers that deal
 * in those environments have their own snippets.
 */
const bootScriptSrc = (siteId: string, product: SiteProduct): string => `${BOOT_BASE_URL}/${product === 'adm' ? 'adm' : 'tag'}/${siteId}`

/** The boot script as a tag to paste in `<head>`. */
export const bootScriptTag = (siteId: string, product: SiteProduct): string => `<script async src="${bootScriptSrc(siteId, product)}"></script>`

/**
 * The same boot script, appended by the page's own javascript.
 *
 * Beside `bootScriptTag` rather than in the frontend that shows it, because the two are one tag in two
 * syntaxes and the publisher pastes whichever suits their stack. Written out by hand at each of the
 * three screens that offered it, the javascript form never learned about `/adm` at all — so a One media
 * connected from the media flyout booted the analytics runtime and its monetisation never ran.
 */
export const bootScriptJs = (siteId: string, product: SiteProduct): string =>
  "var s = document.createElement('script');\n" +
  "s.type = 'text/javascript';\n" +
  `s.src = '${bootScriptSrc(siteId, product)}';\n` +
  's.async = true;\n' +
  '(document.head || document.documentElement).appendChild(s);'

/**
 * Ranks a status, `SITE_STATUSES` being ordered most urgent first. Anything else ranks last: a media whose
 * status was never written has no status to show, not an early one.
 *
 * One normalisation for the three readers — the list's sort, the status filter, and `worstStatus` below.
 * Mongo's `statusOrder` is its mirror, and a second normalisation here would answer a different question
 * for an unknown status than the sort does.
 */
export const statusRankOf = (status: string | undefined): number => {
  const rank = SITE_STATUSES.indexOf(status as SiteStatus)
  return rank === -1 ? SITE_STATUSES.length : rank
}

const worstStatus = (a: SiteStatus, b: SiteStatus): SiteStatus => (statusRankOf(a) <= statusRankOf(b) ? a : b)

/**
 * The one status of the other product that must not pull a media down. A media activated on One before the
 * two products were split apart still carries Node's `to-configure`, and folding that in would read
 * "To configure" on a media One runs fine on. The others need no allowlist: they lose on rank anyway.
 */
export const NOT_FOLDED_IN_STATUS: SiteStatus = 'to-configure'

/**
 * One product's status, in the precedence the media list reads: drift first, then something to deploy.
 *
 * Drift wins because it is the state nobody asked for. A media with one drifted context and one ready one
 * used to read "Ready to deploy", and the row paints that green — so nothing said anything was wrong.
 * Deliberately the inverse of `stackGroupStateOf`, which answers a different question; the reasoning for
 * the divergence lives there.
 */
export const statusOfCounters = (readyToDeploy: number, outOfSync: number): 'out-of-sync' | 'to-deploy' | 'up-to-date' =>
  outOfSync > 0 ? 'out-of-sync' : readyToDeploy > 0 ? 'to-deploy' : 'up-to-date'

/** Everything the media status is derived from. Mirrored in Mongo by `mediaStatusExpression`. */
export type MediaStatusInput = SiteActivationFlags & Pick<Site, 'admStatus' | 'stacksCounts'>

/**
 * The one status a media has: the worst thing true about it, over every product it carries.
 *
 * A media can carry a stack of a product its pages never show — a One media may still hold a `pbs` stack
 * from before Spark ran inside its One stacks — and a drifted stack nobody can see is still a drifted
 * stack. Hiding it from a user who should not see it is the point; hiding it from the status is not.
 *
 * The totals answer what the two status fields cannot: "all live and in sync" and "nothing deployed at all"
 * both arrive here as `up-to-date`. `total === 0` and never `(total ?? 0) === 0` — a media the recount has
 * not reached has no total, and must keep the answer it shows today rather than turn amber across the estate.
 *
 * `undefined` is a real answer: a media whose status was never written has no status to show.
 */
export const mediaStatusOf = (site: MediaStatusInput): SiteStatus | undefined => {
  // The media's own product is the one it is sold on, so One wins both flags — the tie-break `siteProductOf`
  // makes, and an Analytics media keeps One's field, where its activation would be written.
  const onOne = isAdManagementEnabled(site) || !isPbsEnabled(site)
  const own = onOne ? site.admStatus : site.pbsStandaloneStatus
  if (own === undefined || own === 'inactive') return own

  if ((onOne ? site.stacksCounts?.admTotal : site.stacksCounts?.pbsTotal) === 0) return 'to-configure'

  const other = onOne ? site.pbsStandaloneStatus : site.admStatus
  return other && other !== NOT_FOLDED_IN_STATUS ? worstStatus(own, other) : own
}

/**
 * Whether One actually runs on this media, which is the tie-break every "whose inventory is this?"
 * question resolves on.
 *
 * Not `siteProductOf(site) === 'adm'`: that answers `adm` for a both-flagged in-app media too, and One
 * cannot run off-web — so reading it would leave such a media carrying no inventory at all, refusing
 * One's ad units for being off-web and Node's for having lost the tie.
 *
 * Exported because the Prebid Server stack build asks the same question of the same media: a One media gets
 * no Node stack, and the build and the stack creation have to agree on which media that is.
 */
export const oneRunsOnSite = (site: SiteActivationFlags & Pick<Site, 'env'>): boolean => isAdManagementEnabled(site) && site.env === 'web'

/**
 * Whether a media can carry an ad unit, given that the two products keep separate collections.
 *
 * A media takes its inventory from the one product it counts as running on — `siteProductOf`'s winner,
 * One first — and from no other: One's medias carry One's ad units, Node's carry Node's, and an
 * Analytics media carries none, since it deploys nothing.
 *
 * The two flags used to be asked separately, so a media predating the one-product rule carried both
 * and was offered both collections at once. That read as a bug wherever the two meet: a server-side
 * bidder seat mapped on such a media listed One's web ad units and Node's server-side ones side by
 * side, for a media every other screen calls a One media. One wins here for the same reason it wins in
 * `siteProductOf` and `mediaStatusOf` — a media should not answer "whose inventory is this?" one way in
 * the media list and another in a mapping.
 *
 * The ad unit says which collection it came from. That used to be inferred from `env` being absent —
 * true of One's ad units, and of any Node one predating the field, which was then offered on a One media.
 *
 * One's ad units are web by construction — the same reason One is refused on any other environment — so
 * they are offered on a web media and nowhere else. On Node's side `env` narrows further: an in-app
 * media cannot carry an AMP ad unit.
 *
 * Wider than `adUnitSourceOfMedia`, deliberately: this answers what a media *may* be mapped on, which is
 * the right question for a mapping screen. Which single inventory its stack is *built* from is the other
 * one, and narrower.
 */
export const canSiteCarryAdUnit = (site: SiteActivationFlags & Pick<Site, 'env'>, adUnit: AdUnitSource): boolean => {
  const oneRuns = oneRunsOnSite(site)

  return adUnit.product === 'adm' ? oneRuns : !oneRuns && isPbsEnabled(site) && (SITE_AD_UNIT_ENV_COMPATIBILITY[site.env]?.includes(adUnit.env) ?? false)
}

/**
 * The one ad unit environment a Node media's stack draws from.
 *
 * Narrower than `SITE_AD_UNIT_ENV_COMPATIBILITY`, which answers what a media *can* carry: web can carry
 * both `web` and `s2s`, and offering both is what puts two ad units of the same name in front of the
 * user as two rows with nothing to tell them apart.
 *
 * A One media is deliberately not answered here. Its inventory is One's ad units, which are a different
 * collection with no `env` at all — no `AdUnitEnv` describes it, and returning one would name inventory
 * the media does not carry.
 *
 * `env` is nullable on the medias that predate it, and read as web everywhere else, so they fall through
 * to the web branch rather than being answered with nothing.
 */
export const adUnitEnvOfNodeMedia = (site: Pick<Site, 'env'>): AdUnitEnv => {
  if (site.env === 'ios' || site.env === 'android') return 'inapp'
  if (site.env === 'amp') return 'amp'
  return 's2s'
}

/**
 * Which inventory a media's Prebid Server stack is built from.
 *
 * A union rather than an `AdUnitEnv`, because One's ad units live in another collection and no
 * `AdUnitEnv` names them: answering `'web'` would point the caller at Node's web rows, which are not
 * what a One media carries — and there are none, since Node only ever creates `s2s`.
 *
 * Exactly one source per media. `SITE_AD_UNIT_ENV_COMPATIBILITY` answers the wider question of what a
 * media *can* carry, which is the right answer for a mapping screen and the wrong one here: a stack
 * built from everything a media could carry emits in-app codes on a web page.
 */
export type AdUnitSource = { product: 'adm' } | { product: 'pbs'; env: AdUnitEnv }

export const adUnitSourceOfMedia = (site: SiteActivationFlags & Pick<Site, 'env'>): AdUnitSource => (oneRunsOnSite(site) ? { product: 'adm' } : { product: 'pbs', env: adUnitEnvOfNodeMedia(site) })

export type AdmSite = Site & {
  draftStacks: number
  liveStacks?: number
  outOfSyncStacks?: number
  readyToDeployStacks?: number
}

export type NodeCreateSite = Pick<Site, 'name' | 'env'>

const StacksCountsSchema = z.object({
  canCreateUpdatedVersion: z.number().default(0).optional(),
  outOfSync: z.number().default(0).optional(),
  toDeploy: z.number().default(0).optional(),
  live: z.number().default(0).optional(),
  /**
   * Deployable stacks of each product. `mediaStatusOf` reads them to tell "nothing deployed" from
   * "everything deployed and in sync" — the other counters answer both with zeros.
   *
   * No `.default(0)`: absent has to stay distinguishable from zero. A media the status sweep has not
   * reached yet must keep the status it shows today rather than turn into "To configure".
   */
  admTotal: z.number().optional(),
  pbsTotal: z.number().optional(),
})

const SiteSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1, { message: 'Name is required' }),
  enabled: z.boolean().default(true).optional(),
  pbjsVariableName: z.string().optional(),
  adManagementEnabled: z.boolean().optional(),
  pbsStandaloneStatus: SiteStatusSchema.optional(),
  admStatus: SiteStatusSchema.optional(),
  env: SiteEnvSchema.default('web'),
  mappingCountPerBidderSeat: z.record(z.string(), z.number()).optional(),
  stacksCounts: StacksCountsSchema.optional(),
})

export const SiteSchemaWithoutId = SiteSchema.omit({ id: true })

export type StacksCounts = z.infer<typeof StacksCountsSchema>
export type SiteStatus = z.infer<typeof SiteStatusSchema>
export type SiteControlCenterStatus = z.infer<typeof SiteControlCenterStatusSchema>
export type Site = z.infer<typeof SiteSchema>

export const SITE_STATUS_LABELS: Record<SiteStatus, string> = {
  'to-configure': 'To configure',
  'out-of-sync': 'Out of sync',
  'to-deploy': 'Ready to deploy',
  'up-to-date': 'Up to date',
  inactive: 'Not active',
}

export type CreateSite = Omit<Site, 'id' | 'pbsStacks' | 'mappingCountPerBidderSeat' | 'stacksCounts'>

export interface SiteWithStacks extends Site {
  stacks: Stack[]
}

const PublicSiteSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1, { message: 'Name is required' }),
  pbjsVariableName: z.string(),
  script: z.string(),
  prebidServerStatus: SiteStatusSchema.optional(),
  adManagementEnabled: z.boolean().optional(),
  env: SiteEnvSchema.default('web'),
})

export const PublicSiteSchemaWithoutId = SiteSchema.omit({ id: true })
export const CreatePublicSiteSchema = PublicSiteSchema.omit({ id: true, script: true, prebidServerStatus: true })
  .extend({
    enablePrebidServer: z.boolean().optional(),
    createDefaultStack: z.boolean().optional(),
  })
  .partial({ pbjsVariableName: true })

export const UpdatePublicSiteSchema = PublicSiteSchema.omit({ id: true, script: true })

// Schema for update without allowing prebidServerStatus modification
export const UpdatePublicSiteSchemaWithoutPbs = PublicSiteSchema.omit({ id: true, script: true, prebidServerStatus: true })

// Schema for bulk activation of Prebid Server
export const ActivatePrebidServerSchema = z.object({
  siteIds: z.array(z.string()).min(1, { message: 'At least one site ID is required' }),
  createDefaultStack: z.boolean().optional(),
})

export type PublicSite = z.infer<typeof PublicSiteSchema>
export type CreatePublicSite = z.infer<typeof CreatePublicSiteSchema>
export type UpdatePublicSite = z.infer<typeof UpdatePublicSiteSchema>
export type UpdatePublicSiteWithoutPbs = z.infer<typeof UpdatePublicSiteSchemaWithoutPbs>
export type ActivatePrebidServerInput = z.infer<typeof ActivatePrebidServerSchema>
export type AdminSite = {
  id: string
  name: string
  pbjsVariableName: string
  script: string
  scopeId: ScopeId
  enabled: boolean
  adManagementEnabled: boolean
  pbsStandaloneStatus?: SiteStatus
  admStatus?: SiteStatus
  admOnboarding: boolean
  admOnboardingEnabledTime?: number
  env: SiteEnv
}

export type SiteName = string

export const validateName = (siteName: SiteName): InputValidation => {
  if (siteName === '') {
    return { valid: false, errorMessage: 'Name is required' }
  } else {
    return validInput
  }
}

export type ApiKey = {
  id: string
  scopeId: string
  name: string
}

// CSV Import types for PBS-style import
export type CreateSiteFromCSV = {
  name: string
  env: SiteEnv
}

export type SiteBulkCreateResponse = {
  value?: Site
  error?: string
}

export const isInappEnv = (siteEnv: SiteEnv): boolean => {
  return siteEnv === 'ios' || siteEnv === 'android'
}
